import React, {useEffect, useRef, useCallback, useMemo} from 'react';
import SMCTableLoadingRow from './SMCTableLoadingRow';
import SMCTableRow from './SMCTableRow';
import PropTypes from 'prop-types';
import {Box} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {useTranslation} from 'react-i18next';
import {VariableSizeList} from 'react-window';
import {Droppable, Draggable} from 'react-beautiful-dnd';
import clsx from 'clsx';
import SMCPageLoading from './../../../components/core/pageComponents/SMCPageLoading';

function SMCTableBody({
    onScroll,
    width,
    height,
    textToHighlight,
    isDraggable=false,
    content,
    selectedObjects,
    setSelectedObjects,
    onResize,
    columns,
    columnsWidths,
    loadNextPage,
    loading=false,
    hasNextPage=false,
    hasCheckboxColumn=true,
    isRowSelectable,
    rowIdSelector,
    columnsState,
    onClickRow,
    allowRowWrap,
    showEditIcon=true
}) {
    const { t } = useTranslation();
    const bodyRef = useRef(null);
    const classes = useStyles();
    const lastObjectSelected = useRef(null);

    const wasResizeOnLoadHandlerCalled = useRef(false);

    const contentLength = content ? content.length : 0;
    // If there are more items to be loaded, then add an extra row to hold a loading indicator
    const rowCount = (hasNextPage && loading) ? contentLength + 1 : contentLength;

    // Load next page only if
    // - There are more items to be loaded
    // - Another page is not loading now
    // - The last item in the table is at least 50
    //   items away from the last item currently loaded
    const shouldLoadNextPage = (lastIndexInTable) => {
        return (
            hasNextPage &&
            !loading &&
            content && ((content.length - lastIndexInTable) < 50)
        );
    }

    // Maps the heights of rows, used in VariableSizeList to know the height of each row
    const rowHeightsMap = useRef({});
    // Sent to each row, and after its render, will set the height of itself
    const setRowHeight = useCallback((index, size) => {
      rowHeightsMap.current = { ...rowHeightsMap.current, [index]: size < 32 ? 32 : size };
      bodyRef.current && bodyRef.current.resetAfterIndex(index);
    }, []);
    // If row height doesn't exist in map, or row wraps aren't allowed, set as 32
    const getRowHeight = useCallback(index => {
        if (allowRowWrap && rowHeightsMap.current[index]) {
            return rowHeightsMap.current[index];
        } else {
            return 32;
        }
    }, [allowRowWrap]);

    // Resize handler
    // Passes the body div DOM object
    const resizeHandler = useCallback(() => {
        if (bodyRef.current && bodyRef.current._outerRef) {
            onResize(bodyRef.current._outerRef);
        }
    }, [onResize]);

    useEffect(() => {
        resizeHandler();
    }, [height, width, resizeHandler]);

    const tableBodyOnLoad = useCallback(() => {
        // We want to call onResize the first time only
        if (!wasResizeOnLoadHandlerCalled.current) {
            onResize(bodyRef.current._outerRef);
            wasResizeOnLoadHandlerCalled.current = true;
        }
    }, [onResize]);
    
    // Scroll handler
    // Passes the left scroll and top scroll values of the table body
    const onScrollBody = () => {
        if (bodyRef.current && bodyRef.current._outerRef) {
            const bodyOuterRef = bodyRef.current._outerRef;
            onScroll(bodyOuterRef.scrollLeft, bodyOuterRef.scrollTop);
        }
    }

    // Get id of row from its content
    // Use rowIdSelector to get id for row, otherwise, use index
    const getRowId = useCallback((rowContent, index) => {
        let rowId;
        if (rowIdSelector) {
            rowId = rowIdSelector(rowContent, index);
        } else {
            rowId = index;
        }
        return rowId;
    }, [rowIdSelector]);

    // List of row ids in order
    const rowIdList = useMemo(() => {
        return content ? content.map((val, index) => getRowId(val, index)) : null;
    }, [content, getRowId]);

    // Add or remove an object from selected objects
    const rowSelectHandler = useCallback((objectId, e) => {
        if (e.target.checked) {
            setSelectedObjects([...selectedObjects, objectId]);
        } else {
            setSelectedObjects(selectedObjects.filter(item => item !== objectId));
        }
        lastObjectSelected.current = objectId;
    }, [selectedObjects, setSelectedObjects]);

    // Shift click handler
    // Select (or unselect) a content range from the last selected object until the row chosen
    // If last row toggled is selected, select the range, otherwise unselect the range
    const rowShiftClickHandler = useCallback((objectId, e) => {
        if (!rowIdList) {
            return;
        }
        let objectsRange = [];
        if (lastObjectSelected.current) {
            // Object selected beforehand, select range from last object to this current pbject
            const indexOfLastObjectSelected = rowIdList.findIndex(id => id === lastObjectSelected.current);
            const indexOfCurrentObjectSelected = rowIdList.findIndex(id => id === objectId);
            
            if (indexOfLastObjectSelected < indexOfCurrentObjectSelected) {
                objectsRange = rowIdList.slice(indexOfLastObjectSelected+1, indexOfCurrentObjectSelected+1);
            } else if (indexOfLastObjectSelected > indexOfCurrentObjectSelected) {
                objectsRange = rowIdList.slice(indexOfCurrentObjectSelected, indexOfLastObjectSelected)
            }

            // Select or unselect depending on last selected checkbox state
            const isLastObjectSelectedChecked = selectedObjects.includes(lastObjectSelected.current);
            if (isLastObjectSelectedChecked) {
                setSelectedObjects([...selectedObjects, ...objectsRange]);
            } else {
                setSelectedObjects(selectedObjects.filter(obj => !objectsRange.includes(obj)));
            }
        } else {
            // First object selected, normal select
            if (e.target.checked) {
                setSelectedObjects([...selectedObjects, objectId]);
            } else {
                setSelectedObjects(selectedObjects.filter(obj => obj !== objectId));
            }
        }

        lastObjectSelected.current = objectId;
    }, [rowIdList, selectedObjects, setSelectedObjects]);


    // RowGenerator to use with FixedSizedList
    // Return a draggable row, or a loading row if after data's length
    const RowGenerator = React.memo(({data, index, style}) => {
        if (index === data.length) {
            return (
                <SMCTableLoadingRow style={style} />
            );
        } else {
            const rowContent = data[index];
            const rowId = getRowId(rowContent, index);
            const isLastRow = (index === (data.length -1));
            return (
                <Draggable isDragDisabled={!isDraggable} draggableId={rowId + ''} index={index}>
                    {(provided) => (
                        <SMCTableRow
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            innerRef={provided.innerRef}
                            textToHighlight={textToHighlight}
                            columns={columns}
                            columnsState={columnsState}
                            rowNumber={index}
                            content={rowContent}
                            columnsWidths={columnsWidths}
                            selectedObjects={selectedObjects}
                            onCheckboxChange={rowSelectHandler}
                            onCheckboxShiftClick={rowShiftClickHandler}
                            hasCheckboxColumn={hasCheckboxColumn}
                            // Important, keep style last! ({...provided.draggableProps} has a style tag that can overrride)
                            style={{
                                ...style,
                                ...provided.draggableProps.style,
                                borderBottom: isLastRow ? '1px solid #F6F7F9' : undefined
                            }}
                            isSelectable={isRowSelectable}
                            rowId={rowId}
                            setHeight={setRowHeight}
                            onClickRow={onClickRow}
                            allowRowWrap={allowRowWrap}
                            showEditIcon={showEditIcon}
                        />
                    )}
                </Draggable>
            );
        }
    });

    const tableInnerElement = React.forwardRef((props, ref) => (
        <div ref={ref} id="ResultsTableDiv" {...props} />
     ));
     
    return (
        <Box onScroll={onScrollBody} overflow="auto" width={width} height={height} onLoad={tableBodyOnLoad}>
            <div className={classes.table}>
                {
                    (() => {
                        // Has content, or is loading
                        if (content && content.length > 0) {
                            return (
                                <Droppable
                                    isDropDisabled={!isDraggable}
                                    droppableId="droppable"
                                    mode="virtual"
                                    renderClone={
                                        (provided, snapshot, rubric) => {
                                            const rowNum = rubric.source.index;
                                            const rowContent = content[rowNum];
                                            const rowId = getRowId(rowContent, rowNum);
                                            return (
                                                <SMCTableRow
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    innerRef={provided.innerRef}
                                                    textToHighlight={textToHighlight}
                                                    columns={columns}
                                                    rowNumber={rowNum}
                                                    content={rowContent}
                                                    columnsState={columnsState}
                                                    columnsWidths={columnsWidths}
                                                    selectedObjects={selectedObjects}
                                                    onCheckboxChange={rowSelectHandler}
                                                    onCheckboxShiftClick={rowShiftClickHandler}
                                                    isSelectable={isRowSelectable}
                                                    hasCheckboxColumn={hasCheckboxColumn}
                                                    rowId={rowId}
                                                    allowRowWrap={allowRowWrap}
                                                    showEditIcon={showEditIcon}
                                                />
                                            );
                                        }
                                    }
                                >
                                {
                                    (provided) => (
                                        <VariableSizeList
                                            height={height}
                                            itemCount={rowCount}
                                            itemSize={getRowHeight}
                                            width={width}
                                            outerRef={provided.innerRef}
                                            ref={bodyRef}
                                            itemData={content}
                                            innerElementType={tableInnerElement}
                                            overscanCount={5}
                                            estimatedItemSize={32}
                                            layout="vertical"
                                            onItemsRendered={({visibleStopIndex}) => {
                                                if (shouldLoadNextPage(visibleStopIndex)) {
                                                    loadNextPage && loadNextPage();
                                                }
                                            }}
                                        >
                                            {RowGenerator}
                                        </VariableSizeList>
                                    )
                                }
                                </Droppable>
                            );
                        }
                        // Table in loading mode, with no objects loaded yet
                        else if (loading) {
                            return (
                                <SMCPageLoading/>
                            )
                        }
                        // No content, and height over 50
                        else if (height > 50) {
                            return (
                                <div className={clsx(classes.tableBody, "centerContent", classes.noResults)}>
                                    <span>{t('SMC_VIEWCONTROL_NORES')}</span>
                                </div>
                            );
                        }
                        // No content, and height under 50
                        else {
                            return (
                                <div className={classes.tableBody}>
                                    <Box height="100%" paddingTop="5px" paddingLeft="10px" paddingBottom="5px" textAlign="left">
                                        {t('SMP.PAGE.PLAN.DS.ADMIN.EMPTY')}
                                    </Box>
                                </div>
                            )
                        }
                    })()
                }
            </div>
        </Box>
    );
}

const useStyles = makeStyles({
    table: {
        tableLayout: 'fixed',
		width: '100%',
        height: '100%',
        border: 0,
        color: '#384C6E',
        lineHeight: 'initial',
    },
    tableBody: {
        width: '100%',
        height: '100%'
    },
    loadingRoot: {
        height: '100%',
        backgroundColor: '#00000010'
    },
    noResults: {
        fontSize: '32px',
        opacity: '0.9',
        height: '66%'
    }
})

SMCTableBody.propTypes = {
    onScroll: PropTypes.func.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number,
    isDraggable: PropTypes.bool,
    content: PropTypes.arrayOf(PropTypes.object),
    selectedObjects: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    setSelectedObjects: PropTypes.func,
    onCheckboxChange: PropTypes.func,
    onResize: PropTypes.func,
    columns: PropTypes.arrayOf(PropTypes.object),
    loadNextPage: PropTypes.func,
    loading: PropTypes.bool,
    hasNextPage: PropTypes.bool,
    hasCheckboxColumn: PropTypes.bool,
    isRowSelectable: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    rowIdSelector: PropTypes.func,
    allowRowWrap: PropTypes.bool
}

export default React.memo(SMCTableBody);